import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ScoreCard } from '@design-system/Cards/ScoreCard';
import { Separator } from '@design-system/Separator';
import { getBackgroundImageUrl } from '@utils/nutrition/getImageUrl';
import { NavBar } from '@design-system/NavBar';
import {
  NutritionBadgeDetails,
  Badge,
} from '@/components/shared/NutritionBadgeDetails';
import { useSaveSupplementMutation } from '@viome/viome-base/src/usecase/Nutrition/NutritionQuery';
import {
  BookmarkFlag,
  Score,
} from '@viome/viome-base/src/usecase/Nutrition/Nutrition.model';
import { BookmarkButton } from '@/components/shared/BookmarkButton';
import { ScrollArea, ScrollBar } from '@design-system/ScrollArea';
import { BIOTICS, GEL, LOZENGES, SUPPLEMENTS } from '@/constants/images';
import { Image } from '@design-system/Image';

interface IngredientDetailProps {
  title?: string;
  description?: string;
  dosageWithUnit: string;
  groupName: string;
  subDescriptionLabel: string;
  helperCards: Badge[];
  scoreCards?: Score[];
  supportingResearch?: string[];
  ingredientId?: string;
  productId: number[];
  'data-testid'?: string;
}

export const IngredientDetail: React.FC<IngredientDetailProps> = ({
  title,
  description,
  dosageWithUnit,
  groupName,
  subDescriptionLabel,
  helperCards,
  scoreCards,
  supportingResearch,
  ingredientId,
  productId,
  'data-testid': testId,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isBookmarked, setIsBookmarked] = useState<boolean>(
    location?.state?.isBookmarked,
  );

  const PRODUCT_MAP = {
    1: { product_name: 'Oral Lozenges', image_url: LOZENGES },
    2: { product_name: 'Supplements', image_url: SUPPLEMENTS },
    3: { product_name: 'Gut Formula', image_url: BIOTICS },
    4: { product_name: 'Toothpaste & Gel', image_url: GEL },
    5: { product_name: 'Toothpaste & Gel', image_url: GEL },
  } as const;

  const findProductDetails = (productIds: number[]) => {
    const products = productIds.map(id => PRODUCT_MAP[id as keyof typeof PRODUCT_MAP]).filter(Boolean);
    return Array.from(new Map(products.map(item => [item.product_name, item])).values());
  };

  const productDetails = findProductDetails(productId);

  const navigateToConditionsPage = (id: number, scoreType: string) => {
    return navigate(
      scoreType === 'l3' ? `/health/l3/${id}` : `/health/l4/${id}`,
    );
  };

  const handleOnSaveMutationSuccess = () => {
    setIsBookmarked((previousValue) => !previousValue);
  };

  const { mutate, isPending } = useSaveSupplementMutation({
    onSuccess: handleOnSaveMutationSuccess,
  });

  const handleOnBookmark = () => {
    const bookmarkFlag = isBookmarked ? BookmarkFlag.Remove : BookmarkFlag.Add;

    mutate({
      flag: bookmarkFlag,
      product_id: location?.state?.productId,
      ingredient_id: Number(ingredientId),
    });
  };

  return (
    <div className="flex flex-col min-h-screen" data-testid={testId}>
      <div className="flex-grow">
        <section 
          data-testid="ingredientDetail-mainSection"
          className="grid mx-auto container p-4 md:px-8 lg:px-24 lg:py-6 max-w-[960px]"
        >
          {/* Header */}
          <NavBar data-testid="ingredientDetail-navbar" className="md:-ml-5">
            <BookmarkButton
              data-testid="ingredientDetail-bookmarkButton"
              bookmarked={isBookmarked}
              disabled={isPending}
              isPending={isPending}
              onClick={handleOnBookmark}
            />
          </NavBar>

          {/* Title and Description */}
          {title && (
            <h1 
              data-testid="ingredientDetail-title"
              className="typography-d2 text-primary mb-4 mt-20"
            >
              {title}
            </h1>
          )}
          {(groupName || dosageWithUnit) && (
            <p 
              data-testid="ingredientDetail-groupAndDosage"
              className="typography-title text-secondary"
            >
              {groupName}
              {dosageWithUnit && (
                <span className="typography-body"> • {dosageWithUnit}</span>
              )}
            </p>
          )}
          {(productId && productId.length > 0) && (
            <div 
              data-testid="ingredientDetail-productList"
              className='flex flex-col gap-2 mt-3'
            >
              {productDetails.map((product, index) => (
                <div 
                  key={index} 
                  data-testid={`ingredientDetail-product-${index}`}
                  className='flex items-center'
                >
                  <Image
                    width={32}
                    height={32}
                    aria-hidden
                    src={product.image_url}
                    alt={product.product_name || 'product-name'}
                  />
                  <span className="flex items-center typography-body text-tertiary ml-2">
                    In my {product.product_name}
                  </span>
                </div>
              ))}
            </div>
          )}

          {subDescriptionLabel?.length > 0 && (
            <p 
              data-testid="ingredientDetail-subDescription"
              className="typography-body text-[#4D4F64CC] mb-2"
            >
              {subDescriptionLabel}
            </p>
          )}

          {helperCards.length > 0 && (
            <ScrollArea data-testid="ingredientDetail-badgesScrollArea">
              <NutritionBadgeDetails 
                data-testid="ingredientDetail-badges"
                badgeInfo={helperCards} 
              />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}

          {description && (
            <div 
              data-testid="ingredientDetail-description"
              className="typography-h3 font-sans text-tertiary mb-6 mt-8"
            >
              <h3>{description}</h3>
            </div>
          )}

          {/* Scores */}
          {scoreCards && scoreCards?.length > 0 && (
            <div data-testid="ingredientDetail-healthImprovementSection">
              <h2 
                data-testid="ingredientDetail-healthImprovementTitle"
                className="typography-h1 text-primary mb-1"
              >
                How it improves your health
              </h2>
              <div 
                data-testid="ingredientDetail-healthImprovementDescription"
                className='text-tertiary typography-body mb-4'
              >
                See how this ingredient supports your health by helping you improve the scores below.
              </div>
              <div 
                data-testid="ingredientDetail-scoreCardsGrid"
                className="grid lg:grid-cols-2 gap-2.5 md:grid-cols-1 mb-20"
              >
                {scoreCards?.map((score) => (
                  <ScoreCard
                    key={score.id}
                    data-testid={`ingredientDetail-scoreCard-${score.id}`}
                    title={score.name}
                    tag={score.rank}
                    color={score.rank_color || ''}
                    score={Number(score.score)}
                    variant="light"
                    onClick={() => {
                      navigateToConditionsPage(score.id, score.score_type);
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Supporting Research */}
      <div
        data-testid="ingredientDetail-supportingResearchSection"
        className="relative bg-cover bg-center mt-auto"
        style={{
          backgroundImage: `url(${getBackgroundImageUrl('ingredient_details_footer_background.svg')})`,
        }}
      >
        <div className="p-8 grid mx-auto container md:px-8 lg:px-24 !py-24 max-w-[960px]">
          <h2 
            data-testid="ingredientDetail-supportingResearchTitle"
            className="typography-h1 text-white mb-4"
          >
            Supporting research
          </h2>
          <Separator 
            data-testid="ingredientDetail-supportingResearchSeparator"
            className="bg-[#FFFFFF33]" 
          />

          {supportingResearch?.map((link, index) => (
            <div
              key={`${link}-${index}`}
              data-testid={`ingredientDetail-researchLink-${index}`}
              className="flex gap-4 mb-4 pt-4 break-all"
            >
              <div className="w-full">
                <div className="pb-4">
                  <a
                    data-testid={`ingredientDetail-researchUrl-${index}`}
                    href={link}
                    className="text-white typography-h3"
                    target="_blank"
                  >
                    {link}
                  </a>
                </div>

                <Separator className="bg-[#FFFFFF33]" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
